import React, { useEffect, useRef, useState } from 'react';
import { getSocket } from '../utils/socket';
import type { GroupScore, RevealResult } from '../types';
import './PublicScores.css';

interface DeltaNotif {
  id: string;
  groupId: number;
  delta: number;
  color: string;
}

export default function PublicScores() {
  const [scores, setScores] = useState<GroupScore[]>([]);
  const [deltas, setDeltas] = useState<Record<number, number>>({});
  const [notifications, setNotifications] = useState<DeltaNotif[]>([]);
  const [revealBanner, setRevealBanner] = useState<string | null>(null);
  const [gameStatus, setGameStatus] = useState<'waiting' | 'in_progress' | 'ended'>('waiting');
  const prevRanksRef = useRef<Record<number, number>>({});
  const deltaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const socket = getSocket();

    const handleScoresUpdate = ({ scores: s }: { scores: GroupScore[] }) => {
      setScores(s);
    };

    const handleReveal = (data: RevealResult) => {
      setScores(data.scores);

      const newDeltas: Record<number, number> = {};
      const notifs: DeltaNotif[] = [];

      data.results.forEach((r) => {
        if (r.points > 0) {
          newDeltas[r.groupId] = r.points;
          const gs = data.scores.find((s) => s.id === r.groupId);
          notifs.push({
            id: `${r.groupId}-${Date.now()}`,
            groupId: r.groupId,
            delta: r.points,
            color: gs?.color || '#7c3aed',
          });
        }
      });

      setDeltas(newDeltas);
      setNotifications(notifs);

      const banner = data.correctAnswer
        ? `Bonne réponse : ${data.correctAnswer}`
        : data.songTitle
        ? `${data.songTitle}${data.artist ? ` — ${data.artist}` : ''}`
        : null;
      setRevealBanner(banner);

      if (deltaTimeoutRef.current) clearTimeout(deltaTimeoutRef.current);
      deltaTimeoutRef.current = setTimeout(() => {
        setDeltas({});
        setNotifications([]);
        setRevealBanner(null);
      }, 5000);
    };

    const handleGameStarted = () => {
      setGameStatus('in_progress');
      setScores([]);
      setDeltas({});
      setNotifications([]);
    };

    const handleGameEnded = ({ scores: s }: { scores: GroupScore[] }) => {
      setGameStatus('ended');
      setScores(s);
    };

    const handleLobbyOpened = () => {
      setGameStatus('waiting');
      setScores([]);
      setDeltas({});
    };

    socket.on('scores:update', handleScoresUpdate);
    socket.on('question:reveal', handleReveal);
    socket.on('serie:intru_reveal', (data: any) => {
      if (data.scores) setScores(data.scores);
    });
    socket.on('game:started', handleGameStarted);
    socket.on('game:ended', handleGameEnded);
    socket.on('lobby:opened', handleLobbyOpened);

    return () => {
      socket.off('scores:update', handleScoresUpdate);
      socket.off('question:reveal', handleReveal);
      socket.off('serie:intru_reveal');
      socket.off('game:started', handleGameStarted);
      socket.off('game:ended', handleGameEnded);
      socket.off('lobby:opened', handleLobbyOpened);
      if (deltaTimeoutRef.current) clearTimeout(deltaTimeoutRef.current);
    };
  }, []);

  const maxPts = Math.max(...scores.map((s) => s.totalPoints), 1);
  const MEDALS = ['🥇', '🥈', '🥉'];

  return (
    <div className="public-scores">
      <header className="ps-header">
        <div className="ps-logo">🎵</div>
        <h1 className="ps-title">BlindTest</h1>
        <div className="ps-live-badge">
          <span className="ps-live-dot" />
          Scores en direct
        </div>
      </header>

      {revealBanner && (
        <div className="ps-reveal-banner">
          <span className="ps-reveal-icon">🎯</span>
          <span>{revealBanner}</span>
        </div>
      )}

      {scores.length === 0 ? (
        <div className="ps-empty">
          {gameStatus === 'waiting' ? (
            <>
              <div className="ps-empty-icon">⏳</div>
              <p>En attente du début de la partie...</p>
            </>
          ) : (
            <>
              <div className="ps-empty-icon">🎵</div>
              <p>Aucun score pour l'instant</p>
            </>
          )}
        </div>
      ) : (
        <div className="ps-scoreboard">
          {scores.map((s) => {
            const pct = maxPts > 0 ? (s.totalPoints / maxPts) * 100 : 0;
            const delta = deltas[s.id];
            const notif = notifications.find((n) => n.groupId === s.id);

            return (
              <div
                key={s.id}
                className={`ps-row ${s.rank <= 3 ? `ps-rank-${s.rank}` : ''}`}
              >
                <div className="ps-rank-badge">
                  {MEDALS[s.rank - 1] ?? <span className="ps-rank-num">{s.rank}</span>}
                </div>

                <div className="ps-team-info">
                  <div className="ps-team-dot" style={{ background: s.color }} />
                  <span className="ps-team-name">{s.groupName}</span>
                </div>

                <div className="ps-bar-wrap">
                  <div
                    className="ps-bar"
                    style={{
                      width: `${pct}%`,
                      background: s.color,
                      boxShadow: delta ? `0 0 12px ${s.color}80` : 'none',
                    }}
                  />
                </div>

                <div className="ps-pts-wrap">
                  <span className="ps-pts">{s.totalPoints}</span>
                  <span className="ps-pts-label">pts</span>
                  {notif && (
                    <span
                      key={notif.id}
                      className="ps-delta-notif"
                      style={{ color: notif.color }}
                    >
                      +{notif.delta}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {gameStatus === 'ended' && scores.length > 0 && (
        <div className="ps-winner">
          <div className="ps-winner-icon">🏆</div>
          <div className="ps-winner-label">Vainqueur</div>
          <div className="ps-winner-name" style={{ color: scores[0]?.color }}>
            {scores[0]?.groupName}
          </div>
          <div className="ps-winner-pts">{scores[0]?.totalPoints} points</div>
        </div>
      )}

      <footer className="ps-footer">
        <a href="/" className="ps-footer-link">Rejoindre →</a>
      </footer>
    </div>
  );
}
